// Visual Effects Library - Micro-interacciones y animaciones
import React, { useState, useEffect } from 'react';

// =====================================================
// CONFIGURACIÓN DE ANIMACIONES
// =====================================================

export const springConfig = {
  gentle: { stiffness: 120, damping: 14 },
  wobbly: { stiffness: 180, damping: 12 },
  stiff: { stiffness: 260, damping: 20 },
  slow: { stiffness: 280, damping: 60 },
};

// Verificar preferencia de movimiento reducido
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// =====================================================
// EFECTOS CSS (No requieren librerías externas)
// =====================================================

// Clases de animación para usar con className
export const animations = {
  // Fade In/Out
  fadeIn: 'animate-fade-in',
  fadeOut: 'animate-fade-out',
  fadeInUp: 'animate-fade-in-up',
  fadeInDown: 'animate-fade-in-down',
  
  // Scale
  scaleIn: 'animate-scale-in',
  scaleOut: 'animate-scale-out',
  
  // Slide
  slideInRight: 'animate-slide-in-right',
  slideInLeft: 'animate-slide-in-left',
  slideInUp: 'animate-slide-in-up',
  slideInDown: 'animate-slide-in-down',
  
  // Bounce
  bounce: 'animate-bounce',
  bounceIn: 'animate-bounce-in',
  
  // Spin
  spin: 'animate-spin',
  spinSlow: 'animate-spin-slow',
  
  // Pulse
  pulse: 'animate-pulse',
  pulseSlow: 'animate-pulse-slow',
  
  // Shake
  shake: 'animate-shake',
  
  // Wiggle
  wiggle: 'animate-wiggle',
};

// =====================================================
// CONFIGURACIÓN DE VARIANTES DE ANIMACIÓN (Para Framer Motion)
// =====================================================

export const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

export const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

// =====================================================
// HOOKS PARA ANIMACIONES
// =====================================================

// Hook para animación de texto tipo máquina de escribir
export function useTypingAnimation(text, speed = 50) {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    if (!text) {
      setDisplayedText('');
      return;
    }

    if (prefersReducedMotion()) {
      setDisplayedText(text);
      return;
    }

    const characters = Array.from(text);
    setDisplayedText('');
    let index = 0;

    const timer = setInterval(() => {
      if (index >= characters.length) {
        clearInterval(timer);
        return;
      }

      const nextChar = characters[index];
      index += 1;

      setDisplayedText(prev => prev + nextChar);

      if (index >= characters.length) {
        clearInterval(timer);
      }
    }, speed);

    return () => clearInterval(timer);
  }, [text, speed]);

  return displayedText;
}

// Hook para animación de números
export function useCountAnimation(end, duration = 1000) {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (prefersReducedMotion()) {
      setCount(end);
      return;
    }
    
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [end, duration]);
  
  return count;
}

// Hook para animación de entrada en viewport
export function useInViewAnimation(threshold = 0.1) {
  const [ref, setRef] = useState(null);
  const [isInView, setIsInView] = useState(false);
  
  useEffect(() => {
    if (!ref) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold }
    );
    
    observer.observe(ref);
    
    return () => {
      if (ref) observer.unobserve(ref);
    };
  }, [ref, threshold, isInView]);
  
  return [setRef, isInView];
}

// =====================================================
// COMPONENTES DE EFECTOS
// =====================================================

// Indicador de carga con animación
export function LoadingSpinner({ size = 'md', color = 'blue' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };
  
  const colorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    red: 'text-red-600',
    gray: 'text-gray-600',
  };
  
  return (
    <div className={`${sizeClasses[size]} ${animations.spin}`}>
      <svg className={colorClasses[color]} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>
  );
}

// Efecto de onda (ripple) para botones
export function RippleButton({ children, onClick, className = '', ...props }) {
  const [ripples, setRipples] = useState([]);
  
  const handleClick = (e) => {
    if (prefersReducedMotion()) {
      onClick?.(e);
      return;
    }
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const id = Date.now();
    
    setRipples(prev => [...prev, { x, y, id }]);
    
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== id));
    }, 600);
    
    onClick?.(e);
  };
  
  return (
    <button 
      className={`relative overflow-hidden ${className}`} 
      onClick={handleClick}
      {...props}
    >
      {children}
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute bg-white/30 rounded-full pointer-events-none animate-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </button>
  );
}

// Skeleton loader para contenido
export function Skeleton({ className = '', variant = 'text' }) {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700';
  
  const variantClasses = {
    text: 'h-4 rounded',
    title: 'h-8 rounded',
    box: 'rounded-lg',
    circle: 'rounded-full',
  };
  
  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${className}`} />
  );
}

// Componente de transición de página
export function PageTransition({ children, className = '' }) {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    if (!prefersReducedMotion()) {
      setShow(true);
    }
  }, []);
  
  return (
    <div className={`${show ? animations.fadeInUp : 'opacity-0'} transition-all duration-300 ${className}`}>
      {children}
    </div>
  );
}

// =====================================================
// CLASES CSS PERSONALIZADAS (agregar a styles.css)
// =====================================================

export const customAnimationStyles = `
/* Animaciones personalizadas */
@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-down {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slide-in-right {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes bounce-in {
  0% {
    opacity: 0;
    transform: scale(0.3);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  70% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
}

@keyframes wiggle {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-3deg); }
  75% { transform: rotate(3deg); }
}

@keyframes ripple {
  to {
    width: 300px;
    height: 300px;
    opacity: 0;
  }
}

/* Removed neon glow animations */

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes borderStream {
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
}

/* Removed generic glow animation */

/* Clases de animación */
.animate-fade-in { animation: fade-in 0.3s ease-in-out; }
.animate-fade-in-up { animation: fade-in-up 0.4s ease-out; }
.animate-fade-in-down { animation: fade-in-down 0.4s ease-out; }
.animate-scale-in { animation: scale-in 0.3s ease-out; }
.animate-slide-in-right { animation: slide-in-right 0.4s ease-out; }
.animate-bounce-in { animation: bounce-in 0.6s ease-out; }
.animate-shake { animation: shake 0.5s ease-in-out; }
.animate-wiggle { animation: wiggle 0.3s ease-in-out; }
.animate-ripple { animation: ripple 0.6s ease-out; }
.animate-spin-slow { animation: spin 3s linear infinite; }
.animate-pulse-slow { animation: pulse 3s ease-in-out infinite; }

/* Transiciones suaves */
.transition-colors { transition: color 0.2s, background-color 0.2s; }
.transition-transform { transition: transform 0.2s; }
.transition-all { transition: all 0.2s; }

/* Hover effects */
.hover-lift:hover { transform: translateY(-2px); }
.hover-grow:hover { transform: scale(1.05); }
.hover-bright:hover { filter: brightness(1.1); }

/* Glassmorphism */
.glass {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.glass-dark {
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

/* Glow effects - OLD */
.glow-blue {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
}

.glow-green {
  box-shadow: 0 0 20px rgba(34, 197, 94, 0.5);
}

.glow-red {
  box-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
}

/* NEW: Neon Effects for Dark Mode */
.neon-border {
  position: relative;
  z-index: 0;
}

.neon-border::before,
.neon-border::after {
  content: '';
  position: absolute;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity 0.3s ease;
  opacity: 0;
}

.dark .neon-border::before {
  inset: 0;
  padding: 1px;
  background: linear-gradient(135deg,
    #3b82f6 0%,
    #8b5cf6 30%,
    #ec4899 60%,
    #ef4444 100%);
  background-size: 400% 400%;
  animation: borderStream 12s ease infinite;
  z-index: -1;
  opacity: 0.75;
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

/* Removed neon-border after glow */

.neon-border:hover::before,
.neon-border:hover::after {
  opacity: 1;
}

/* Removed neon glow/text helpers */

/* Light Mode Enhancements */
.gradient-bg {
  background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
  background-size: 400% 400%;
  animation: gradientShift 15s ease infinite;
}

.glass-effect {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15);
}

.dark .glass-effect {
  background: rgba(17, 24, 39, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.3);
}

.floating {
  animation: floating 3s ease-in-out infinite;
}

@keyframes floating {
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
}

/* Interactive hover effects */
/* Removed hover glow helpers */

/* Enhanced shadows */
/* Removed shadow-glow */

.shadow-soft {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.dark .shadow-soft {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.3), 
    0 2px 4px -1px rgba(0, 0, 0, 0.2),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);
}

/* Dark mode neon buttons */
/* Removed neon button styles */

/* Animated gradient borders */
.gradient-border {
  position: relative;
  background: white;
  border-radius: 12px;
  padding: 1px;
}

.gradient-border::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 12px;
  padding: 2px;
  background: linear-gradient(45deg, 
    #3b82f6, #8b5cf6, #ec4899, 
    #ef4444, #f59e0b, #10b981, #3b82f6);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
}

/* Light mode pastel animations */
.pastel-gradient {
  background: linear-gradient(-45deg, #ffeaa7, #fab1a0, #74b9ff, #a29bfe);
  background-size: 400% 400%;
  animation: gradientShift 20s ease infinite;
}

.shimmer {
  position: relative;
  overflow: hidden;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  100% {
    left: 100%;
  }
}

.dark .shimmer::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(59, 130, 246, 0.3),
    transparent
  );
}

/* Removed sidebar glow */
`;

// =====================================================
// UTILIDADES
// =====================================================

// Función para aplicar animación con retraso
export function staggerChildren(children, delay = 100) {
  return children.map((child, index) => ({
    ...child,
    props: {
      ...child.props,
      style: {
        ...child.props.style,
        animationDelay: `${index * delay}ms`,
      },
    },
  }));
}

// Función para crear efecto de confeti
export function createConfetti(count = 50) {
  const container = document.createElement('div');
  container.className = 'fixed inset-0 pointer-events-none z-50';
  
  for (let i = 0; i < count; i++) {
    const confetti = document.createElement('div');
    confetti.className = 'absolute w-2 h-3';
    confetti.style.left = `${Math.random() * 100}%`;
    confetti.style.backgroundColor = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'][Math.floor(Math.random() * 5)];
    confetti.style.animation = `fall ${Math.random() * 2 + 1}s linear`;
    container.appendChild(confetti);
  }
  
  document.body.appendChild(container);
  
  setTimeout(() => {
    container.remove();
  }, 3000);
}

// CSS para confeti (agregar a styles)
export const confettiStyles = `
@keyframes fall {
  to {
    transform: translateY(100vh) rotate(360deg);
  }
}
`;
